// Geo + currency helpers used on the public landing page so we can show
// money examples in the visitor's local currency without requiring them
// to be signed in or to pick a currency manually.

export type LandingCurrency = 'USD' | 'EUR' | 'GBP' | 'DKK';

// EU member states (ISO-3166 alpha-2). Used to decide whether to format
// illustrative amounts in EUR. Denmark is intentionally excluded — it
// uses DKK below.
const EU_COUNTRIES = new Set<string>([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO',
  'SK', 'SI', 'ES', 'SE',
]);

export function countryToCurrency(country: string | null | undefined): LandingCurrency {
  if (!country) return 'USD';
  const c = country.toUpperCase();
  if (c === 'DK') return 'DKK';
  if (c === 'GB' || c === 'UK') return 'GBP';
  if (EU_COUNTRIES.has(c)) return 'EUR';
  return 'USD';
}

// Illustrative figures on the landing page are *re-denominated*, not
// converted — we keep the same numeral and only swap the currency
// marker. We use a uniform comma thousands grouping for every currency
// so the numbers look identical across locales (e.g. "500", "2,029").
//
// Symbol placement matches everyday writing conventions for each
// currency: $, € and £ are written before the amount, while the
// Danish krone uses the "kr" suffix after the amount (e.g. "500 kr").
export function formatLandingAmount(amount: number, currency: LandingCurrency): string {
  let numerals: string;
  try {
    numerals = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount);
  } catch {
    numerals = Math.round(amount).toLocaleString('en-US');
  }
  switch (currency) {
    case 'DKK': return `${numerals} kr`;
    case 'EUR': return `€${numerals}`;
    case 'GBP': return `£${numerals}`;
    default:    return `$${numerals}`;
  }
}
